/* ==========================================================================
   ac-carrusel.js — Carrusel continuo, infinito y arrastrable.
   --------------------------------------------------------------------------
   Se engancha a cualquier `[data-ac-carrusel]`. Hoy lo usan las reseñas.

   QUÉ HACE
   Mueve la pista despacio hacia la izquierda sin parar. Al llegar al final del
   primer juego de tarjetas, vuelve al principio SIN salto: la pista lleva dos
   copias del mismo contenido y el desplazamiento se reinicia justo cuando la
   segunda copia ocupa la posición exacta de la primera. Nadie ve la costura.

   Si el visitante arrastra con el dedo o con el ratón, el movimiento se para
   mientras arrastra y se reanuda solo, con un respiro de 2,5 s para que le dé
   tiempo a leer lo que fue a buscar.

   POR QUÉ ASÍ Y NO CON `overflow-x: auto`
   Antes era un carril con scroll nativo. Eso pintaba una barra de scroll a la
   vista —fea en móvil— y no se podía animar solo. Aquí no hay scroll: hay una
   pista movida por `transform`, y la barra desaparece porque ya no existe.

   CÓMO SE MANTIENEN LOS 60 fps
   · Solo se toca `transform`, nunca `left` ni `scrollLeft`. `translate3d` deja
     el trabajo en la GPU y no provoca recálculo de maquetación.
   · Dentro del bucle NO se lee nada del DOM. Los anchos se miden una vez al
     arrancar y se vuelven a medir solo al cambiar el tamaño de la ventana.
   · Un único `requestAnimationFrame` por carrusel, y se detiene entero cuando
     la sección no está en pantalla: un carrusel invisible no gasta batería.
   · El avance se calcula por TIEMPO transcurrido, no por fotograma. Si el
     móvil baja a 40 fps, la velocidad se ve igual; lo único que cambia es la
     suavidad, no el ritmo.

   ACCESIBILIDAD
   · Con `prefers-reduced-motion` no se mueve nada: se deja el carril con
     scroll normal, que sigue siendo usable.
   · La copia clonada va con `aria-hidden` para que un lector de pantalla no
     lea las reseñas dos veces.
   · Al enfocar con el teclado cualquier elemento de dentro, se para.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCIR = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var PAUSA_TRAS_SOLTAR = 2500; // ms de respiro después de arrastrar

  function montar(caja) {
    // Idempotente: el editor de Shopify recarga secciones y no queremos dos
    // bucles moviendo la misma pista en direcciones distintas.
    if (caja.dataset.acCarruselMontado === '1') return;
    caja.dataset.acCarruselMontado = '1';

    var pista = caja.querySelector('[data-ac-pista]');
    if (!pista) return;

    var original = pista.firstElementChild;
    if (!original) return;

    if (REDUCIR) {
      // Sin movimiento: se deja el carril deslizable a mano y se acabó.
      caja.classList.add('is-estatico');
      return;
    }

    // La segunda copia. `inert` además de `aria-hidden` para que no reciba foco.
    var copia = original.cloneNode(true);
    copia.setAttribute('aria-hidden', 'true');
    copia.querySelectorAll('a, button, input, summary, [tabindex]').forEach(function (n) {
      n.setAttribute('tabindex', '-1');
    });
    pista.appendChild(copia);

    caja.classList.add('is-vivo');

    var velocidad = parseFloat(caja.dataset.acVelocidad) || 28; // px por segundo
    var x = 0;
    var anchoJuego = 0;
    var enPantalla = true;
    var pausas = 0; // contador: hover, arrastre, foco… puede haber varias a la vez
    var ultimo = 0;
    var lazo = null;

    function medir() {
      // Ancho de UN juego de tarjetas, incluido el hueco entre ellas.
      var estilo = window.getComputedStyle(pista);
      var hueco = parseFloat(estilo.columnGap || estilo.gap) || 0;
      anchoJuego = original.getBoundingClientRect().width + hueco;
    }

    function pintar(t) {
      if (!ultimo) ultimo = t;
      var dt = (t - ultimo) / 1000;
      ultimo = t;

      // Un salto de pestaña puede devolver un `dt` enorme; se acota para que
      // el carrusel no pegue un tirón al volver.
      if (dt > 0.1) dt = 0.1;

      if (!pausas && anchoJuego > 0) {
        x -= velocidad * dt;
        // La costura: cuando se ha recorrido un juego entero, se vuelve atrás
        // exactamente ese ancho. La segunda copia queda donde estaba la
        // primera y visualmente no ocurre nada.
        if (x <= -anchoJuego) x += anchoJuego;
        pista.style.transform = 'translate3d(' + x + 'px,0,0)';
      }

      lazo = window.requestAnimationFrame(pintar);
    }

    function arrancar() {
      if (lazo) return;
      ultimo = 0;
      lazo = window.requestAnimationFrame(pintar);
    }

    function parar() {
      if (!lazo) return;
      window.cancelAnimationFrame(lazo);
      lazo = null;
    }

    var pausar = function () { pausas++; };
    var reanudar = function () { pausas = Math.max(0, pausas - 1); };

    // ---- Arrastre con el dedo o con el ratón ----------------------------
    var arrastrando = false;
    var xInicio = 0;
    var xPartida = 0;
    var temporizador = null;

    caja.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      arrastrando = true;
      xInicio = e.clientX;
      xPartida = x;
      pausar();
      window.clearTimeout(temporizador);
      caja.classList.add('is-arrastrando');
      caja.setPointerCapture(e.pointerId);
    });

    caja.addEventListener('pointermove', function (e) {
      if (!arrastrando) return;
      x = xPartida + (e.clientX - xInicio);
      // Se normaliza para que arrastrando mucho no se salga del bucle.
      if (anchoJuego > 0) {
        while (x <= -anchoJuego) x += anchoJuego;
        while (x > 0) x -= anchoJuego;
      }
      pista.style.transform = 'translate3d(' + x + 'px,0,0)';
    });

    var soltar = function (e) {
      if (!arrastrando) return;
      arrastrando = false;
      caja.classList.remove('is-arrastrando');
      if (e && e.pointerId != null && caja.hasPointerCapture(e.pointerId)) {
        caja.releasePointerCapture(e.pointerId);
      }
      // Respiro antes de volver a moverse: si reanudara al instante, la
      // reseña que el visitante fue a buscar se le escaparía de la mano.
      temporizador = window.setTimeout(reanudar, PAUSA_TRAS_SOLTAR);
    };

    caja.addEventListener('pointerup', soltar);
    caja.addEventListener('pointercancel', soltar);

    // Un arrastre no debe dispararle el enlace que hay debajo del dedo.
    caja.addEventListener('click', function (e) {
      if (Math.abs(x - xPartida) > 6 && caja.classList.contains('is-arrastrando')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // ---- Pausas de cortesía ---------------------------------------------
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      caja.addEventListener('mouseenter', pausar);
      caja.addEventListener('mouseleave', reanudar);
    }
    caja.addEventListener('focusin', pausar);
    caja.addEventListener('focusout', reanudar);

    // ---- Solo gasta trabajo si se está viendo ---------------------------
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entradas) {
        enPantalla = entradas[0].isIntersecting;
        if (enPantalla) arrancar();
        else parar();
      }, { threshold: 0 }).observe(caja);
    } else {
      arrancar();
    }

    // ---- Remedida al cambiar el tamaño ----------------------------------
    var remedir;
    window.addEventListener('resize', function () {
      window.clearTimeout(remedir);
      remedir = window.setTimeout(function () {
        medir();
        x = 0;
        pista.style.transform = 'translate3d(0,0,0)';
      }, 150);
    });

    medir();
    // Las tarjetas llevan fotos: hasta que no cargan, el ancho medido es otro.
    window.addEventListener('load', medir);
    caja.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', medir, { once: true });
    });

    arrancar();
  }

  function iniciar(raiz) {
    (raiz || document).querySelectorAll('[data-ac-carrusel]').forEach(montar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { iniciar(); }, { once: true });
  } else {
    iniciar();
  }

  // El editor de Shopify recarga secciones sueltas por AJAX.
  document.addEventListener('shopify:section:load', function (e) { iniciar(e.target); });
})();
